class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn) {
          try {
            trapFocus(containerToTrapFocusOn, focusElement);
          } catch (error) {
            console.error('cart-drawer: trapFocus failed', error);
          }
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Refresh the free-shipping bar every time the drawer opens.
    if (window.simsaFreeShipping) {
      try {
        window.simsaFreeShipping.update();
      } catch (error) {
        console.error('cart-drawer: simsaFreeShipping.update failed', error);
      }
    }
  }

  close() {
    // Restore scrolling FIRST, unconditionally, so a failure in the
    // focus-trap cleanup below can never leave the page stuck in a
    // scroll-locked ("can't scroll / stuck on mobile view") state.
    document.body.classList.remove('overflow-hidden');
    this.classList.remove('active');

    try {
      removeTrapFocus(this.activeElement);
    } catch (error) {
      console.error('cart-drawer: removeTrapFocus failed', error);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState) return;

    /*
     * 1. UPDATE CART DRAWER
     */
    const drawer = this.querySelector('.drawer__inner');

    if (drawer) {
      drawer.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    /*
     * 2. UPDATE CART DRAWER + CART ICON
     */
    this.getSectionsToRender().forEach((section) => {
      const sectionElement =
        document.getElementById(section.id) ||
        (section.selector
          ? document.querySelector(section.selector)
          : null);

      if (!sectionElement) return;

      const sectionHTML = parsedState.sections?.[section.id];

      if (!sectionHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    /*
     * 3. IMPORTANT:
     * Make sure the new cart icon is still connected
     * after replacing cart-icon-bubble HTML.
     */
    this.setHeaderCartIconAccessibility();

    /*
     * 4. Re-bind overlay because drawer HTML may have changed.
     */
    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    /*
     * 5. OPEN UPDATED CART DRAWER
     */
    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = parsedHTML.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}